import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ManagingEditor = () => {
  const [editorData, setEditorData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEditorData();
  }, []);

  const fetchEditorData = async () => {
    try {
      // Simulated API call - replace with actual endpoint
      const response = await axios.get('/api/managing-editor');
      setEditorData(response.data);
    } catch (err) {
      // Use placeholder data if API fails
      setEditorData({
        name: "Dr. Fatima Abubakar Mohammed",
        title: "Managing Editor",
        position: "Senior Lecturer in Crop Science",
        department: "Department of Crop Science",
        institution: "Federal University Wukari",
        email: "managing.editor@fuwukari.edu.ng",
        phone: "+234 XXX XXX XXXX",
        biography: "Dr. Fatima Abubakar Mohammed is a Senior Lecturer in Crop Science with extensive experience in manuscript management and editorial processes. She holds a Ph.D. in Crop Science from Ahmadu Bello University and has served as Managing Editor for FUWJALS since 2020.",
        qualifications: [
          "Ph.D. Crop Science, Ahmadu Bello University, Zaria (2015)",
          "M.Sc. Crop Science, University of Nigeria, Nsukka (2008)",
          "B.Sc. Agriculture, Bayero University, Kano (2005)"
        ],
        responsibilities: [
          "Day-to-day editorial operations management",
          "Manuscript tracking and workflow coordination",
          "Communication with authors, reviewers, and editors",
          "Quality assurance of editorial processes",
          "Production coordination and timeline management",
          "Reviewer database maintenance and management"
        ],
        expertise: [
          "Manuscript Management Systems",
          "Editorial Process Optimization",
          "Quality Control and Assurance",
          "Academic Publishing Standards",
          "Peer Review Coordination",
          "Author and Reviewer Relations"
        ],
        achievements: [
          "Editorial Excellence Award, FUWJALS (2022)",
          "Best Practice in Manuscript Management (2021)",
          "Outstanding Service Award, Federal University Wukari (2020)"
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row mb-5">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4 text-center">Managing Editor</h1>
          
          <div className="card mb-5">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 text-center mb-4">
                  <div className="bg-light rounded-circle mx-auto mb-3" style={{width: '150px', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <i className="fas fa-user fa-4x text-muted"></i>
                  </div>
                  <h3>{editorData?.name}</h3>
                  <p className="text-muted">{editorData?.title}</p>
                </div>
                <div className="col-md-8">
                  <h4>Contact Information</h4>
                  <p><strong>Position:</strong> {editorData?.position}</p>
                  <p><strong>Department:</strong> {editorData?.department}</p>
                  <p><strong>Institution:</strong> {editorData?.institution}</p>
                  <p><strong>Email:</strong> <a href={`mailto:${editorData?.email}`}>{editorData?.email}</a></p>
                  <p><strong>Phone:</strong> {editorData?.phone}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Biography</h3>
              <p className="card-text">{editorData?.biography}</p>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Educational Qualifications</h3>
              <ul className="list-unstyled">
                {editorData?.qualifications?.map((qualification, index) => (
                  <li key={index} className="mb-2">
                    <i className="fas fa-graduation-cap text-primary me-2"></i>
                    {qualification}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Key Responsibilities</h3>
              <div className="row">
                <div className="col-12">
                  <ul className="list-unstyled">
                    {editorData?.responsibilities?.map((responsibility, index) => (
                      <li key={index} className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        {responsibility}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Editorial Expertise</h3>
              <div className="row">
                {editorData?.expertise?.map((skill, index) => (
                  <div key={index} className="col-md-6 mb-2">
                    <span className="badge bg-primary me-2">
                      <i className="fas fa-star me-1"></i>
                      {skill}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Awards and Recognition</h3>
              <ul className="list-unstyled">
                {editorData?.achievements?.map((achievement, index) => (
                  <li key={index} className="mb-2">
                    <i className="fas fa-award text-warning me-2"></i>
                    {achievement}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Editorial Services</h3>
              <div className="row">
                <div className="col-md-6">
                  <h4>For Authors</h4>
                  <ul>
                    <li>Manuscript submission guidance</li>
                    <li>Status update inquiries</li>
                    <li>Revision and formatting assistance</li>
                    <li>Publication timeline information</li>
                    <li>Technical support</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>For Reviewers</h4>
                  <ul>
                    <li>Review invitation coordination</li>
                    <li>Manuscript assignment management</li>
                    <li>Timeline and deadline reminders</li>
                    <li>System access support</li>
                    <li>Recognition and feedback</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Office Hours and Availability</h3>
              <div className="row">
                <div className="col-md-6">
                  <h4>Regular Hours</h4>
                  <p>Monday - Friday: 9:00 AM - 5:00 PM (WAT)</p>
                  <p>Response time: Within 24-48 hours</p>
                </div>
                <div className="col-md-6">
                  <h4>Emergency Contact</h4>
                  <p>For urgent editorial matters:</p>
                  <p>Email: urgent@fuwukari.edu.ng</p>
                  <p>Phone: +234 XXX XXX XXXX</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Message from the Managing Editor</h3>
              <p className="card-text">
                "As Managing Editor of FUWJALS, I am committed to ensuring a smooth and efficient 
                editorial process for all our authors and reviewers. My role involves coordinating 
                the day-to-day operations of the journal, from initial submission to final publication.
              </p>
              <p className="card-text">
                I work closely with our editorial team to maintain the highest standards of scholarly 
                publishing while providing excellent service to the global research community. 
                Whether you are a first-time author or an experienced researcher, I am here to 
                support you throughout the publication process.
              </p>
              <p className="card-text">
                Please don't hesitate to contact me with any questions about manuscript submission, 
                review status, or journal policies. I am dedicated to making your publishing 
                experience with FUWJALS as positive and productive as possible."
              </p>
              <p className="text-end">
                <strong>Dr. Fatima Abubakar Mohammed</strong><br />
                <em>Managing Editor, FUWJALS</em>
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <a href={`mailto:${editorData?.email}`} className="btn btn-primary me-3">
              Contact Managing Editor
            </a>
            <a href="/submit-paper" className="btn btn-outline-secondary">
              Submit Manuscript
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagingEditor;